// Apolo IDE — Learning Loop L6: renderer-side vault commit pump.
//
// Pulls `learning_candidates` rows whose status is `approved` from the
// Worker, writes the proposed note into the Obsidian vault via Brain MCP
// (DECISIONS-learning-loop.md D-002 — the Worker cannot reach Brain MCP),
// then PATCHes the candidate to `applied` on success or `failed` with an
// error_message on failure.
//
// For skills it additionally calls POST /api/learning/skills/registry so
// the Worker can index the new auto-skill (L4 activation router).
//
// All HTTP goes through `apiFetch` so the bearer token is picked up
// automatically. All vault I/O goes through `window.apolo.mcp.call` —
// never raw fs (renderer is sandboxed anyway).

import { apiFetch } from '../api.js';

const DEFAULT_MCP_SERVER_ID = 'obsidian';
const FALLBACK_MEMORY_DIR = 'Sistema/Memory/auto';
const FALLBACK_SKILL_DIR = 'Sistema/Skills/auto';

function getMcpBridge() {
  if (typeof window === 'undefined') return null;
  return window.apolo?.mcp || null;
}

function fallbackPath(candidate) {
  const base = candidate.candidate_type === 'skill' ? FALLBACK_SKILL_DIR : FALLBACK_MEMORY_DIR;
  const safeId = (candidate.id || `${Date.now()}`).toString().slice(0, 24).replace(/[^a-z0-9-]/gi, '-');
  return `${base}/auto-${safeId}.md`;
}

function isCandidateActionable(candidate) {
  if (!candidate || typeof candidate !== 'object') return false;
  if (candidate.candidate_type !== 'memory' && candidate.candidate_type !== 'skill') {
    return false;
  }
  if (typeof candidate.proposed_content !== 'string' || candidate.proposed_content.length === 0) {
    return false;
  }
  return true;
}

function extractSkillName(noteContent, fallback) {
  if (typeof noteContent !== 'string') return fallback || null;
  // Try YAML frontmatter `name: foo` line — same shape the Worker emits.
  const m = noteContent.match(/^name:\s*([^\n\r]+)/m);
  if (m && m[1]) return m[1].trim();
  return fallback || null;
}

async function patchCandidateStatus(candidateId, payload) {
  // Worker uses POST /candidates/:id/approve to mark approved. From
  // approved → applied (or failed) goes through a different code path
  // because PATCH only accepts decision transitions. Use the registry
  // endpoint for skills (it side-effects status='applied') and an
  // internal helper for memories — we add a dedicated endpoint server-side
  // in this epic that lifts a candidate from 'approved' to 'applied'.
  return apiFetch(`/api/learning/candidates/${candidateId}/applied`, {
    method: 'POST',
    body: payload,
  });
}

async function markCandidateFailed(candidateId, errorMessage) {
  return apiFetch(`/api/learning/candidates/${candidateId}/failed`, {
    method: 'POST',
    body: { error_message: errorMessage?.slice?.(0, 500) ?? 'unknown' },
  });
}

async function registerSkill({ candidateId, name, vaultPath }) {
  return apiFetch('/api/learning/skills/registry', {
    method: 'POST',
    body: { name, vault_path: vaultPath, candidate_id: candidateId },
  });
}

async function writeNoteViaMcp({ bridge, serverId, vaultPath, content }) {
  if (!bridge?.call) {
    throw new Error('Brain MCP bridge unavailable (renderer not in Electron)');
  }
  // obsidian_write_note signature follows the Brain MCP contract used
  // elsewhere in the codebase (path, content). Server returns whatever
  // the MCP gives; we treat absence of throw as success.
  return bridge.call(serverId, 'obsidian_write_note', { path: vaultPath, content });
}

async function moveNoteToTrashViaMcp({ bridge, serverId, vaultPath }) {
  if (!bridge?.call) {
    throw new Error('Brain MCP bridge unavailable');
  }
  // Convention: prepend `_trash/` to the path, keep the rest of the
  // hierarchy so we can audit later. Matches the worker-side trash_vault_path.
  const trashPath = vaultPath.startsWith('_trash/') ? vaultPath : `_trash/${vaultPath}`;
  await bridge.call(serverId, 'obsidian_move_note', { from: vaultPath, to: trashPath });
  return trashPath;
}

/**
 * Process a single approved candidate end-to-end.
 *
 * Returns `{ ok: true, status: 'applied', vault_path }` on success,
 * `{ ok: false, error }` on failure. Safe to call repeatedly with the
 * same candidate — the Worker enforces idempotency on status transitions
 * (a second POST to /applied after the row is already 'applied' returns
 * 200 with status:'applied' unchanged).
 */
export async function commitCandidate(candidate, { serverId = DEFAULT_MCP_SERVER_ID, bridge } = {}) {
  if (!isCandidateActionable(candidate)) {
    return { ok: false, error: 'candidate not actionable' };
  }
  const resolvedBridge = bridge || getMcpBridge();
  if (!resolvedBridge) {
    return { ok: false, error: 'mcp bridge unavailable' };
  }

  const vaultPath = candidate.applied_vault_path || fallbackPath(candidate);

  try {
    await writeNoteViaMcp({
      bridge: resolvedBridge,
      serverId,
      vaultPath,
      content: candidate.proposed_content,
    });
  } catch (e) {
    const message = e?.message || String(e) || 'mcp write failed';
    await markCandidateFailed(candidate.id, message);
    return { ok: false, error: message, vault_path: vaultPath };
  }

  if (candidate.candidate_type === 'skill') {
    const name = extractSkillName(candidate.proposed_content, candidate.summary);
    try {
      const res = await registerSkill({
        candidateId: candidate.id,
        name: name || `auto-skill-${candidate.id}`,
        vaultPath,
      });
      if (!res.ok) {
        const message = res.error || 'skill registry failed';
        await markCandidateFailed(candidate.id, message);
        return { ok: false, error: message, vault_path: vaultPath };
      }
      return { ok: true, status: 'applied', vault_path: vaultPath, skill_id: res.data?.skill_id };
    } catch (e) {
      const message = e?.message || 'skill registry failed';
      await markCandidateFailed(candidate.id, message);
      return { ok: false, error: message, vault_path: vaultPath };
    }
  }

  // Memory: just lift status to 'applied'.
  const res = await patchCandidateStatus(candidate.id, { vault_path: vaultPath });
  if (!res.ok) {
    return { ok: false, error: res.error || 'failed to mark applied', vault_path: vaultPath };
  }
  return { ok: true, status: 'applied', vault_path: vaultPath };
}

/**
 * Roll back an applied skill: move the note to `_trash/` via Brain MCP,
 * then POST the skill rollback endpoint so D1 records the new trash path.
 */
export async function rollbackSkill(skill, { serverId = DEFAULT_MCP_SERVER_ID, bridge } = {}) {
  if (!skill || !skill.id || !skill.vault_path) {
    return { ok: false, error: 'skill missing id or vault_path' };
  }
  const resolvedBridge = bridge || getMcpBridge();
  if (!resolvedBridge) {
    return { ok: false, error: 'mcp bridge unavailable' };
  }
  let trashPath;
  try {
    trashPath = await moveNoteToTrashViaMcp({
      bridge: resolvedBridge,
      serverId,
      vaultPath: skill.vault_path,
    });
  } catch (e) {
    return { ok: false, error: e?.message || 'mcp move failed' };
  }
  const res = await apiFetch(`/api/learning/skills/${skill.id}/rollback`, {
    method: 'POST',
    body: { trash_vault_path: trashPath },
  });
  if (!res.ok) {
    return { ok: false, error: res.error || 'rollback registry failed', trash_vault_path: trashPath };
  }
  return { ok: true, status: 'rolled_back', trash_vault_path: trashPath };
}

/**
 * Pull all approved candidates and run `commitCandidate` against each.
 *
 * Returns `{ processed, succeeded, failed, results }`. Designed to be
 * polled by the Learning Panel on a slow interval (~5–10s) while the
 * panel is mounted. The PATCH side enforces idempotency so re-running
 * with the same input is safe.
 */
export async function pumpApprovedCandidates({ serverId = DEFAULT_MCP_SERVER_ID, bridge, fetchImpl } = {}) {
  const list = await (fetchImpl
    ? fetchImpl()
    : apiFetch('/api/learning/candidates?status=approved'));
  if (!list.ok) {
    return { processed: 0, succeeded: 0, failed: 0, error: list.error, results: [] };
  }
  const candidates = list.data?.candidates || [];
  let succeeded = 0;
  let failed = 0;
  const results = [];
  for (const candidate of candidates) {
    const res = await commitCandidate(candidate, { serverId, bridge });
    results.push({ id: candidate.id, ...res });
    if (res.ok) succeeded += 1;
    else failed += 1;
  }
  return { processed: candidates.length, succeeded, failed, results };
}

// Exposed for tests so unit tests can stub them without monkey-patching
// the network layer. Production code does not import these.
export const __internals = {
  isCandidateActionable,
  fallbackPath,
  extractSkillName,
  patchCandidateStatus,
  markCandidateFailed,
  registerSkill,
};
